import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { FaPrint, FaSearch, FaFilter } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const API_BASE = "https://nissi-india.com/api";

const PurchaseReqReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [reqNo, setReqNo] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [projectName, setProjectName] = useState("");
  const [store, setStore] = useState("");
  const [status, setStatus] = useState("Waiting");
  const [location, setLocation] = useState("");

  const [storeOptions, setStoreOptions] = useState([]);
  const [projectNameOptions, setProjectNameOptions] = useState([]);
  const [projectCodeOptions, setProjectCodeOptions] = useState([]);
  const [results, setResults] = useState([]);

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = (localStorage.getItem("branchType") || "").toUpperCase(); // HO / BRANCH
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // branchType for UI button: "HO" or "Branch"
  const initialBranchType = branch === "HO" ? "HO" : "Branch";
  const [branchType, setBranchType] = useState(initialBranchType);

  useEffect(() => {
    setBranchType(initialBranchType);
    setLocation(initialBranchType === "HO" ? "HO" : "BRANCH");
  }, [initialBranchType]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const handleBranchTypeChange = (value) => {
    setBranchType(value);
    setLocation(value === "HO" ? "HO" : "BRANCH");
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate(new Date().toISOString().split("T")[0]);
    setReqNo("");
    setProjectCode("");
    setProjectName("");
    setStore("");
    setStatus("Waiting");
    setResults([]);
  };

  const handlePrint = (item) => {
    const id = item.fld_PurchaseRequestid;
    const printUrl = `/inventory/Purchase_Print?id=${id}`;
    window.open(printUrl, "_blank", "noopener,noreferrer");
  };

  const handleEnquiryPrint = (item) => {
    const id = item.fld_PurchaseRequestid;
    const printUrl = `/inventory/Purchase_EnquiryPrint?id=${id}`;
    window.open(printUrl, "_blank", "noopener,noreferrer");
  };

  // Load Store options
  useEffect(() => {
    const fetchStoreOptions = async () => {
      try {
        const res = await axios.post(
          `${API_BASE}/getFromStoreOptions`,
          {
            branchtype: branch || "HO",
            branchid: branchId,
          },
          {
            headers: commonHeaders,
          }
        );

        setStoreOptions(res.data.storeOptions || []);
      } catch (error) {
        toast.error("Failed to load store options");
      }
    };

    if (branchId) {
      fetchStoreOptions();
    }
  }, [branchId, branch, commonHeaders]);

  // Load Project Name options
  useEffect(() => {
    const fetchProjectNames = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/prReport_getProjectName?projectId=Select`,
          {
            headers: commonHeaders,
          }
        );
        setProjectNameOptions(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load project names");
      }
    };

    fetchProjectNames();
  }, [commonHeaders]);

  // Load Project Code options
  useEffect(() => {
    const fetchProjectCodes = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/prReport_getProjectCode?projectId=Select`,
          {
            headers: commonHeaders,
          }
        );
        setProjectCodeOptions(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load project codes");
      }
    };

    fetchProjectCodes();
  }, [commonHeaders]);

  const handleSearch = async () => {
    if (!store) {
      toast.warn("Please select a Store");
      return;
    }

    const params = {
      branchType: location,
      branchId,
      userBranchType: location,
      brType: branchType === "HO" ? "H" : "B",
      reqNo,
      fromDate: fromDate ? formatDate(fromDate) : "",
      toDate: toDate ? formatDate(toDate) : "",
      fromStoreId: store,
      projectId: projectCode || projectName || 0,
      status,
    };

    try {
      const res = await axios.get(`${API_BASE}/purchaseStoreMaterialList`, {
        params,
        headers: commonHeaders,
      });

      setResults(res.data.data || []);
      toast.success("Data loaded successfully");
    } catch (err) {
      toast.error("Failed to fetch listing");
    }
  };

  const handleViewAll = async () => {
    try {
      const res = await axios.get(`${API_BASE}/purchaseStoreMaterialList`, {
        params: {
          branchId,
          branchType: location || "HO",
          userBranchType: location || "HO",
          brType: branchType === "HO" ? "H" : "B",
          reqNo: "",
          fromDate: "",
          toDate: "",
          fromStoreId: "Select",
          projectId: 0,
          status: "Select",
        },
        headers: commonHeaders,
      });

      setResults(res.data.data || []);
      toast.success("All records loaded");
    } catch (err) {
      toast.error("Failed to fetch all records");
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="PURCHASE / STORE / MATERIAL REQUISITION REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Purchase Requisition Report
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {results.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-700" +
                    (showFilters ? " bg-gray-700" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-100" +
                    (showFilters ? " bg-gray-100" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Branch Type
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => handleBranchTypeChange("HO")}
              disabled={branch !== "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors
                ${
                  branchType === "HO"
                    ? `${buttonClass} ${hoverClass} text-white`
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
                ${branch !== "HO" ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Head Office
            </button>

            <button
              onClick={() => handleBranchTypeChange("Branch")}
              disabled={branch === "HO"}
              className={`px-6 py-2 rounded-lg font-medium transition-colors
                ${
                  branchType === "Branch"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
                ${branch === "HO" ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Branch
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Search Filters
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Req No
                </label>
                <input
                  type="text"
                  value={reqNo}
                  onChange={(e) => setReqNo(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="Enter request number"
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Project Code
                </label>
                <select
                  value={projectCode}
                  onChange={(e) => {
                    setProjectCode(e.target.value);
                    setProjectName("");
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select Project Code</option>
                  {projectCodeOptions.map((option, idx) => (
                    <option key={idx} value={option.id}>
                      {option.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Project Name
                </label>
                <select
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(e.target.value);
                    setProjectCode("");
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select Project Name</option>
                  {projectNameOptions.map((option, idx) => (
                    <option key={idx} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Store <span className="text-red-500">*</span>
                </label>
                <select
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select Store</option>
                  {storeOptions.map((s) => (
                    <option key={s.fld_storeid} value={s.fld_storeid}>
                      {s.fld_storename}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Waiting">Waiting</option>
                  <option value="Cancel">Cancel</option>
                  <option value="Close">Close</option>
                  <option value="Approved">Approved</option>
                  <option value="DisApproved">DisApproved</option>
                </select>
              </div>
            </div>

            <div
              className={`flex justify-between items-center pt-4 border-t ${
                isDark ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <button
                onClick={clearFilters}
                className={`px-4 py-2 font-medium ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Clear Filters
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
                >
                  <FaSearch className="w-4 h-4" />
                  Search
                </button>
                <button
                  onClick={handleViewAll}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
                >
                  <FaSearch className="w-4 h-4" />
                  View All
                </button>
              </div>
            </div>
          </div>
        )}

        {results.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="p-4 border-b border-gray-200">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Requisition Results ({results.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Sl No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Req No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Indent Ref</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Request Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Quotation Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Account Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">MD Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">PO Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Print</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Enquiry</th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y divide-gray-200 ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  {results.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {idx + 1}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {item.fld_purchaseRequestNo}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {item.fld_date}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {item.fld_type}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {item.fld_indentref}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                        item.purchaseorderstatus === "Approved"
                          ? "text-green-600"
                          : item.purchaseorderstatus === "DisApproved"
                          ? "text-red-600"
                          : isDark
                          ? "text-yellow-400"
                          : "text-yellow-600"
                      }`}>
                        {item.purchaseorderstatus}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                        item.quotationstatus === "Approved"
                          ? "text-green-600"
                          : item.quotationstatus === "DisApproved"
                          ? "text-red-600"
                          : isDark
                          ? "text-yellow-400"
                          : "text-yellow-600"
                      }`}>
                        {item.quotationstatus}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                        item.accountsstatus === "Approved"
                          ? "text-green-600"
                          : item.accountsstatus === "DisApproved"
                          ? "text-red-600"
                          : isDark
                          ? "text-yellow-400"
                          : "text-yellow-600"
                      }`}>
                        {item.accountsstatus}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                        item.mdstatus === "Approved"
                          ? "text-green-600"
                          : item.mdstatus === "DisApproved"
                          ? "text-red-600"
                          : isDark
                          ? "text-yellow-400"
                          : "text-yellow-600"
                      }`}>
                        {item.mdstatus}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                        item.purchaseorderstatus === "Approved"
                          ? "text-green-600"
                          : item.purchaseorderstatus === "DisApproved"
                          ? "text-red-600"
                          : isDark
                          ? "text-yellow-400"
                          : "text-yellow-600"
                      }`}>
                        {item.purchaseorderstatus}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handlePrint(item)}
                          className={`p-2 rounded transition-colors ${
                            isDark
                              ? "text-blue-400 hover:text-blue-300 hover:bg-gray-700"
                              : "text-blue-600 hover:text-blue-800 hover:bg-gray-100"
                          }`}
                          title="Print Requisition"
                        >
                          <FaPrint className="w-4 h-4" />
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleEnquiryPrint(item)}
                          className={`p-2 rounded transition-colors ${
                            isDark
                              ? "text-green-400 hover:text-green-300 hover:bg-gray-700"
                              : "text-green-600 hover:text-green-800 hover:bg-gray-100"
                          }`}
                          title="Print Enquiry"
                        >
                          <FaPrint className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          showFilters && (
            <div
              className={`text-center py-12 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <p>
                No records found. Please use the filters to search for purchase
                requisitions.
              </p>
            </div>
          )
        )}
      </div>
    </LayoutWrapper>
  );
};

export default PurchaseReqReport;